import React, { useMemo } from 'react';
import { Ring, Roll, dollars, money, pct0, paceTone } from './Ring';
import FocusPanel from './FocusPanel';

/* "Lodged this month" hero + "What to focus on" panel. Every figure is live system data. */
export default function OverviewHero({ boardData, live, month, pulseKey }) {
  const target = Number(boardData?.monthly_target_amount || 0);
  const lodged = Number(boardData?.monthly_lodged_amount || 0);
  const lodgedN = Number(boardData?.monthly_lodged_count || 0);
  const approved = Number(boardData?.approved_loan_amount || 0);
  const gci = Number(boardData?.approved_gci_amount || 0);
  const brokerage = Number(boardData?.total_brokerage_amount || 0);
  const weekTarget = Number(boardData?.weekly_lodgement_target || 0);
  const board = boardData?.board || {};
  const { atRisk, overdue, active } = useMemo(() => {
    let a = 0, o = 0, n = 0;
    [1, 2, 3, 4].forEach((w) => (board[w] || []).forEach((d) => {
      n += 1;
      const h = (d.credit?.health || '').toUpperCase();
      if (h === 'OVERDUE') o += 1; else if (h === 'AT RISK') a += 1;
    }));
    return { atRisk: a, overdue: o, active: n };
  }, [board]);

  const p = target ? lodged / target : 0;
  const remain = Math.max(0, target - lodged);
  const elapsed = live?.elapsed ?? 0;
  const bd = live?.business_days;
  const monthLabel = useMemo(() => {
    const [y, m] = (month || '').split('-').map(Number);
    const d = new Date(y, (m || 1) - 1, 1);
    return d.toLocaleDateString('en-AU', { month: 'long', year: 'numeric' }) + (live?.is_current && bd ? ` · day ${bd.done} of ${bd.total}` : '');
  }, [month, live, bd]);

  return (
    <div className="gpj-grid gpj-hero" data-testid="gpj-hero">
      <section className="gpj-panel" data-m="lodged">
        <div className="gpj-panel-h"><h2>Lodged this month</h2><p>{monthLabel}</p></div>
        <div className="gpj-hero-main">
          <Ring p={p} size={200} value={pct0(p)} of={`${money(lodged)} of ${money(target, 0)}`} tone={paceTone(p, elapsed)} label="Lodged vs target"
            sub={remain ? `<b>${money(remain)}</b> to go` : '<b>Target reached</b>'} testid="gpj-ring-lodged" />
          <div className="gpj-facts">
            <div className="gpj-fact headline">
              <div className="gpj-big" data-testid="gp-month-lodged"><Roll value={dollars(lodged)} /></div>
              <div className="gpj-big-sub">{lodgedN} files{target ? ` · target ${dollars(target)}` : ' · set a target in Set Goals'}</div>
            </div>
            <div className={`gpj-fact remain${remain <= 0 && target ? ' done' : ''}`}><span>Still to lodge</span><strong><Roll value={dollars(remain)} /></strong></div>
            <div className="gpj-fact"><span>Weekly target</span><strong><Roll value={`${weekTarget} /wk`} /></strong></div>
            <div className="gpj-fact"><span>At risk · overdue</span><strong data-testid="gp-atrisk" style={{ color: overdue ? 'var(--red)' : atRisk ? 'var(--amber)' : 'var(--fg)' }}>{atRisk} · {overdue}</strong></div>
            <div className="gpj-fact"><span>Approved this month</span><strong data-testid="gp-approved-amount" style={{ color: 'var(--green)' }}><Roll value={dollars(approved)} /></strong></div>
            <div className="gpj-fact"><span>Approved GCI</span><strong data-testid="gp-approved-gci"><Roll value={dollars(gci)} /></strong></div>
            <div className="gpj-fact"><span>Total approved brokerage</span><strong data-testid="gp-total-brokerage"><Roll value={dollars(brokerage)} /></strong></div>
            <div>
              <div className="gpj-track"><div className="gpj-fill" style={{ width: `${Math.min(100, p * 100)}%` }} /></div>
              <div className="gpj-cap"><span>{pct0(p)} of monthly lodgement target</span><span>{target ? (remain ? `${dollars(remain)} to go` : 'Target reached') : `${active} active files`}</span></div>
            </div>
          </div>
        </div>
      </section>

      <FocusPanel live={live} />
    </div>
  );
}
